import { useEffect, useState } from "react";
import "../styles/myplan.css";
import { fetchPlanRank, fetchRankDetail } from "../api/myplan";
import moment from "moment";
import { Button, Card, CardBody, CardHeader, Table } from "reactstrap";
import Flatpickr from "react-flatpickr";
import Loader from "./Common/Loader";
import { DataPointTypes } from "../types";
import { NoResult } from "./Common/NoResult";
import { jumpTo } from "./UrlModal";
// import AiSummary from "./AiSummary";
import { useSelector } from "react-redux";
import { selectUtility } from "../store/utility/selector";
import { RankChart } from "./Charts/RankChart";
import UsageBtnGroup from "./UsageBtnGroup";
import { Typography } from "@mui/material";
import { selectLocation } from "../store/location/selector";
import MyTooltip from "./Common/Tooltip";

interface PlanRankDetail {
  base_rate: string;
  date: string;
  pd_id: string;
  plan_id: string;
  rank: number;
}

interface ChartSeries {
  [key: string]: DataPointTypes;
}

type PlanRank = any;
interface GroupedPlans {
  title: string;
  plans: PlanRank[];
}

const today = moment().format("YYYY-MM-DD");

const MyPlanRank = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [rankTableData, setRankTableData] = useState<PlanRank>([]);
  const [termTableData, setTermTableData] = useState<Array<GroupedPlans>>([])
  const [filterValue, setFilterValue] = useState({
    price: "500",
    created_ts: today,
  });
  const [chartSeries, setChartSeries] = useState<ChartSeries>({});
  const [dataStringObj, setDataStringObj] = useState({});
  const currentLocation = useSelector(selectLocation);
  const currentUtility = useSelector(selectUtility);

  useEffect(() => {
    const getPlanRank = async () => {
      setIsLoading(true);
      setDataStringObj({});

      fetchPlanRank({
        utility_id: currentUtility.utility_id,
        ...filterValue,
      }).then((res) => {
        setIsLoading(false);
        if (res && res.status === 1) {
          const data = sortData(res.response)
          setRankTableData(res.response);
          setTermTableData(data);
        }
        else {
          setRankTableData([]);
          setTermTableData([]);
        }
      });
    };

    if (currentUtility) {
      getPlanRank();
    }
  }, [filterValue, currentUtility]);

  useEffect(() => {
    if (rankTableData.length === 0) return;

    for (let item of rankTableData) {
      getRankData(item.term, filterValue.price, "", item.utility_name);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankTableData]);

  const getRankData = (
    term: string,
    price: string,
    source: string,
    utility_name: string
  ) => {
    fetchRankDetail(
      currentUtility.utility_id,
      term,
      price,
      source,
      filterValue.created_ts
    ).then((res) => {
      let each: DataPointTypes[] = [];
      if (res && res.status === 1) {
        const { response } = res;
        response.forEach((data: PlanRankDetail) => {
          if (
            data.rank !== 0 &&
            moment(data.date, "MM/DD/YYYY").isAfter(
              moment(filterValue.created_ts, "YYYY-MM-DD").subtract(1, "month")
            )
          ) {
            each.push({
              x: data.date,
              y: data.rank,
              detail: data,
            });
          }
        });
      }
      const _dataString = `
                ##${utility_name} Rand Trend - Term: ${term}
                ${JSON.stringify(
                  res?.response?.filter((item: any) => item.rank !== 0)
                )}
            `;
      setDataStringObj((prevState) => ({ ...prevState, [term]: _dataString }));
      setChartSeries((prevState: any) => ({ ...prevState, [term]: each }));
    });
  };

  const changePrice = (val: any) => {
    setFilterValue(Object.assign({}, filterValue, { price: val }));
  };

  const sortData = (plans:PlanRank[]) => {
    const numericPlans = plans.map(plan => ({
      ...plan,
      termNum: parseInt(plan.term, 10)
      }));

      const longTermPlans = numericPlans.filter(plan => plan.termNum >= 12);
      const shortTermPlans = numericPlans.filter(plan => plan.termNum < 12);

      longTermPlans.sort((a, b) => a.termNum - b.termNum); 
      shortTermPlans.sort((a, b) => b.termNum - a.termNum);

      const formatPlans = (plans: Array<PlanRank & { termNum: number }>) => 
          plans.map(({ termNum, ...rest }) => rest);

      return [
          {
              title: "Term ≥ 12",
              plans: formatPlans(longTermPlans)
          },
          {
              title: "Term < 12",
              plans: formatPlans(shortTermPlans)
          }
      ];
  }

  const changeDate = (val: any) => {
    setFilterValue(
      Object.assign({}, filterValue, {
        created_ts: moment(val).format("YYYY-MM-DD"),
      })
    );
  };

  const displayRaw = () => {
    if (isLoading && rankTableData.length === 0) return <Loader />;
    if (rankTableData.length > 0 && termTableData.length > 0) {
      return (
        <>
          {termTableData.map((group: any) => (
            <div key={group.title} className="plan-group">
              <h3 className="group-title" style={{fontSize:'16px', color:"#878A99"}}>{group.title}</h3>
              {group.plans
                .filter((plan: any) => !["48", "60"].includes(plan.term))
                .map((plan: any) => (
                  <Row
                    key={`${group.title}-${plan.term}-${plan.plan_id}`} 
                    data={plan}
                    chartData={ chartSeries[plan.term] ? chartSeries[plan.term] : ([] as any)}
                    isLoading={isLoading}
                    handleChangeSource={(term: string ,source: string) => {
                      getRankData(
                        plan.term,
                        filterValue.price,
                        source,
                        plan.utility_name
                      );
                    }}
                    dataString={(dataStringObj as any)[plan.term]}
                  />
                ))}
            </div>
          ))}
        </>
      );
    }
    return <NoResult />;
  };

  return (
    <>
      <Card>
        <CardHeader className="align-items-center d-flex">
          <div className="card-title mb-0 flex-grow-1">
            <h4 className="card-title mb-0 flex-grow-1">Ranking on Select Shopping Sites</h4>
            {currentLocation && currentUtility && (
              <Typography
                variant="subtitle2"
                className="text-muted"
              >{`${currentLocation.state_name} - ${currentUtility.utility_name}`}</Typography>
            )}
          </div>
          <MyTooltip>
            <h4 style={{ color: "#fff"}}>Source Rank Description</h4>
            <p>1. MARKET: Ranking calculated from all plans we captured in the market</p>
            <p>2. COMPAREPOWER: Ranking among all plans on <a href="http://comparepower.com/">comparepower.com</a></p>
            <p>3. ELECTRICITY: Ranking among all plans on <a href="http://electricityrates.com/">electricityrates.com</a></p>
            <p>4. TEXASELECTRICITYRATINGS: Ranking among all plans on <a href="http://texaselectricrates.com/">texaselectricrates.com</a></p>
            <p>5. ENERGYBOT: Ranking among all plans on <a href="http://energybot.com/">energybot.com</a></p>
          </MyTooltip>
        </CardHeader>
        <CardBody>
          <div className="mb-4 d-flex justify-content-between">
            <UsageBtnGroup
              onChangeValue={(val: string) => changePrice(val)}
              isLoading={isLoading}
            />
            <Flatpickr
              className="rank_datepicker"
              value={filterValue.created_ts}
              options={{
                dateFormat: "Y-m-d",
                defaultDate: [today],
                minDate: moment().startOf("year").format("YYYY-MM-DD"),
                maxDate: today,
                allowInput: true,
              }}
              onChange={([newValue]: any) => {
                changeDate(newValue);
              }}
            />
          </div>
          <Flatpickr
            className="mobile_rank_datepicker"
            value={filterValue.created_ts}
            options={{
              dateFormat: "Y-m-d",
              defaultDate: [today],
              minDate: moment().startOf("year").format("YYYY-MM-DD"),
              maxDate: today,
              allowInput: true,
            }}
            onChange={([newValue]: any) => {
              changeDate(newValue);
            }}
          />
          {displayRaw()}
        </CardBody>
      </Card>
    </>
  );
};

const Row = ({
  data,
  isLoading,
  chartData,
  handleChangeSource,
  dataString,
}: {
  data: any;
  isLoading: boolean;
  chartData: DataPointTypes[];
  handleChangeSource: Function;
  dataString: string;
}) => {
  const [selectedSource, setSelectedSource] = useState("market");

  const { term, source } = data;

  return (
    <Card>
      <CardHeader className="d-flex justify-content-between fs-16">
        <div>Term: {term}</div>
      </CardHeader>
      {/* <AiSummary data={dataString} /> */}
      <CardBody className="row">
        <div className="col-lg-7 col-12">
          <RankChart
            title={`Rank Trend by ${selectedSource.toLocaleUpperCase()}`}
            data={
              chartData?.length > 0
                ? [{ id: `Term ${term}`, data: chartData }]
                : []
            }
            isLoading={isLoading}
          />
        </div>
        <div className="col-lg-5 col-12 rank_trend_market_table">
          <Table className="table-borderless">
            <thead>
              <tr>
                <th></th>
                <th>Source</th>
                <th>Lowest Rate</th>
                <th className="text-end">Rank</th>
              </tr>
            </thead>
            <tbody>
              {source &&
                Object.keys(source).map((idx) => {
                  if (!source[idx].rate) return null;
                  return (
                    <tr
                      key={idx}
                      style={{
                        cursor: "pointer",
                        backgroundColor:
                          selectedSource === idx ? "#f7f7f7" : "transparent",
                      }}
                      onClick={() => {
                        if (selectedSource === idx) return;
                        setSelectedSource(idx);
                        handleChangeSource(
                          term,
                          idx === "market" ? "" : source[idx].source
                        );
                      }}
                    >
                      <td className="pe-0">
                        {selectedSource === idx && (
                          <i className="fs-20 me-2 bx bx-chevrons-right text-secondary" />
                        )}
                      </td>
                      <td>
                        <span className="text-uppercase">{idx}</span>
                      </td>
                      <td>
                        <span>{source[idx].plan_name}</span>
                        <br />
                        <span>
                          <b>{source[idx].rate + "¢"}</b>
                          {source[idx].plan_id && (
                            <Button
                              onClick={() =>
                                jumpTo(source[idx].plan_id, source[idx].pd_id)
                              }
                              outline
                              rel="noreferrer"
                              className="btn btn-soft-secondary btn-sm ms-2"
                            >
                              EFL
                            </Button>
                          )}
                        </span>
                      </td>
                      <td className="text-end">
                        <b>{source[idx].rank}</b>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </Table>
        </div>
      </CardBody>
    </Card>
  );
};

export default MyPlanRank;
